import { TOOLTIP_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { useRef } from 'react';

import { useTooltip } from './useTooltip';
import Button, { ButtonSize, ButtonType } from '../button/button';

export default function Tooltip({
  tooltipId,
  noResizeClosing,
  isSingleLineText,
  isWideContent,
  showCloseButton,
  headerContent,
  contentAlignment = 'center',
  showOverlay,
  closeCallbackFC,
}: {
  tooltipId: string;
  noResizeClosing?: boolean;
  isSingleLineText?: boolean;
  isWideContent?: boolean;
  showCloseButton?: boolean;
  headerContent?: React.ReactNode;
  contentAlignment?: 'center' | 'left';
  showOverlay?: boolean;
  closeCallbackFC?: () => void;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<HTMLDivElement>(null);
  const { title, description, top, left, isOpen, closeTooltipHandler, pointerLeft, pointerTop } = useTooltip(
    tooltipRef,
    pointerRef,
    tooltipId,
    noResizeClosing,
    closeCallbackFC,
  );

  let descriptionWidthClass = 'max-w-[168px] md:max-w-200';
  if (isSingleLineText) {
    descriptionWidthClass = 'max-w-none break-words';
  } else if (isWideContent) {
    descriptionWidthClass = 'max-w-[338px]';
  }

  return (
    <div
      className={cn(
        'fixed w-full h-full top-0 left-0',
        isOpen ? 'visible z-[100]' : 'invisible',
        showOverlay ? 'bg-gray-800/30' : 'bg-transparent',
      )}
      role="button"
      tabIndex={0}
      onClick={closeTooltipHandler}
      onKeyDown={e => {
        onEnter(e, () => closeTooltipHandler());
      }}
    >
      <div
        className={cn(
          'absolute z-[100] bg-white rounded-[8px] shadow-[rgba(137,137,137,0.3)_0px_5px_10px]',
          isOpen ? 'visible' : 'invisible',
        )}
        style={{ top: top, left: left }}
        ref={tooltipRef}
        data-testid={`${TOOLTIP_TEST_IDS.CONTAINER}-${tooltipId}`}
      >
        {(showCloseButton || headerContent) && (
          <div className={cn('flex items-center pr-8 pb-0 pl-16', showCloseButton ? 'pt-0' : 'pt-12')}>
            {headerContent}
            {showCloseButton && (
              <div className="ml-auto">
                <Button onClick={closeTooltipHandler} type={ButtonType.IconOnlyRound} size={ButtonSize.Medium} noBorder>
                  <i className="susu-icons" translate="no">
                    close
                  </i>
                </Button>
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            'flex flex-col justify-center mx-16 mb-16 gap-4',
            showCloseButton || headerContent ? 'mt-8' : 'mt-14',
            contentAlignment === 'left' ? 'text-left' : 'text-center',
          )}
        >
          <div
            className="font-medium text-[13px] leading-xs break-words md:text-sm md:leading-sm"
            data-testid={`${TOOLTIP_TEST_IDS.TITLE}-${tooltipId}`}
          >
            {title}
          </div>
          <div
            className={cn('leading-xs text-xs md:text-[13px]', descriptionWidthClass)}
            data-testid={`${TOOLTIP_TEST_IDS.DESCRIPTION}-${tooltipId}`}
          >
            {description}
          </div>
        </div>
      </div>
      <div
        ref={pointerRef}
        className="absolute w-0 h-0 border-solid border-b-0 border-l-[8px] border-r-[8px] border-t-[6px] border-l-transparent border-r-transparent border-t-white"
        style={{ top: pointerTop, left: pointerLeft }}
      />
    </div>
  );
}
